import { useMemo } from 'react'

import type { ErrorLike } from '@apollo/client'

import { readFragment } from '~publish/graphql'

import { isNotNull, isOfType } from '~publish/helpers/typeGuards'
import {
  FeedsForSidebar_Collection,
  FeedsForSidebar_Feed,
  useFeedsForSidebar,
} from '~publish/pages/Create/Feed/hooks/useFeedsForSidebar'

import type { CollectionData, FeedData } from '../types'

interface UseParsedFeedsDataResult {
  collections: CollectionData[]
  feeds: FeedData[]
  existingFeeds: { id: string; url: string }[]
  totalFeeds: number
  loading: boolean
  error: ErrorLike | undefined
}

export function useParsedFeedsData(
  organizationId: string | undefined,
): UseParsedFeedsDataResult {
  const {
    collections: collectionsData,
    feeds: feedsData,
    totalFeeds,
    error,
    loading,
  } = useFeedsForSidebar({
    organizationId,
  })

  const collections = useMemo(() => {
    const rawCollections = readFragment(
      FeedsForSidebar_Collection,
      collectionsData,
    )
    return rawCollections.map((collection) => {
      const collectionFeeds =
        collection.feeds
          ?.map((feedData) => {
            const feed = readFragment(FeedsForSidebar_Feed, feedData)
            return isOfType(
              feed.connectionMetadata,
              'RSSFeedConnectionMetadata',
            )
              ? {
                  id: feed.id,
                  url: feed.connectionMetadata.url,
                  name: feed.name,
                  lastRefreshed: feed.lastRefreshed,
                }
              : null
          })
          .filter(isNotNull) ?? []

      return {
        id: collection.id,
        name: collection.name,
        feeds: collectionFeeds,
      }
    })
  }, [collectionsData])

  const feeds = useMemo(() => {
    return feedsData
      .map((feedData) => {
        const feed = readFragment(FeedsForSidebar_Feed, feedData)
        return isOfType(feed.connectionMetadata, 'RSSFeedConnectionMetadata')
          ? {
              id: feed.id,
              url: feed.connectionMetadata.url,
              name: feed.name,
              lastRefreshed: feed.lastRefreshed,
            }
          : null
      })
      .filter(isNotNull)
  }, [feedsData])

  const existingFeeds = useMemo(() => {
    const rootFeeds = feeds.map((f) => ({ id: f.id, url: f.url }))
    const collectionFeeds = collections.flatMap((c) =>
      c.feeds.map((f) => ({ id: f.id, url: f.url })),
    )
    return [...rootFeeds, ...collectionFeeds]
  }, [feeds, collections])

  return {
    collections,
    feeds,
    existingFeeds,
    totalFeeds,
    loading,
    error,
  }
}
